import {Button, Chips, DialogFooter, Tooltip} from 'platform/components';
import {Box, HStack, Icon, Show, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import styled from 'styled-components';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  AdvertisementStatusEnum,
  useGetVehicleLocalizedContentQuery,
  type GetAdvertisementPreviewApiResponse,
  type GetSalesPlatformsApiRes,
} from '@dms/api/sales';
import type {AdvertisingPlatformCodeEnum, GetVehicleApiResponse} from '@dms/api/shared';
import type {GetVehicleApiResponse as GetVehicleV2ApiResponse} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {useVehicleCatalogue} from '@dms/shared';

import {parseDate, type Nullish, type RequiredTestIdProps} from 'shared/web';

import {getLanguageContentStatus} from '../../../utils/getLanguageContentStatus';
import {PREVIEW_CONTAINER_BREAKPOINTS} from '../constants/PREVIEW_CONTAINER_BREAKPOINTS';
import {PREVIEW_STICKY_Z_INDEX} from '../constants/PREVIEW_STICKY_Z_INDEX';
import {getPreviewContainerBreakpoint} from '../utils/getPreviewContainerBreakpoint';
import {AdvertisementPreviewContent} from './AdvertisementPreviewContent';
import type {AdvertisementPreviewPhotosQuery} from './AdvertisementPreviewGallery';
import {
  LanguageOptionLabel,
  LanguageStatusDot,
  VisuallyHiddenLabel,
} from './AdvertisementPreviewLanguageSwitcher';
import {PlatformTabsScroller} from './PlatformTabsScroller';

const VISIBLE_PLATFORM_COUNT = 8;

type AdvertisementPreviewBodyProps = RequiredTestIdProps & {
  preview: {
    data: GetAdvertisementPreviewApiResponse;
    photosQuery: AdvertisementPreviewPhotosQuery;
    platformTitles: Map<AdvertisingPlatformCodeEnum, string>;
    salesPlatforms?: GetSalesPlatformsApiRes;
    vehicleCatalogue: ReturnType<typeof useVehicleCatalogue>[0];
    isVehicleCatalogueLoading: boolean;
    pricingVehicle?: GetVehicleApiResponse;
    specificationsVehicle?: GetVehicleV2ApiResponse;
  };
  selection: {
    activePlatformCode?: AdvertisingPlatformCodeEnum;
    requestedLanguage?: string;
    localizedContentByLanguage: Record<
      string,
      NonNullable<ReturnType<typeof useGetVehicleLocalizedContentQuery>['data']>[number]
    >;
  };
  hasLanguageTabs: boolean;
  isLanguageSwitchFetch: boolean;
  inAdvertisement: boolean;
  hasUpdatePermission: boolean;
  hasPublishPermission: boolean;
  isPublishBlocked: boolean;
  isSubmitting: boolean;
  onSubmit: () => void;
  onPlatformChange: (platformCode: AdvertisingPlatformCodeEnum) => void;
  onLanguageChange: (language?: string) => void;
  onAddPhotos: () => void;
  onClose: () => void;
  vehicleId: string;
};

export function AdvertisementPreviewBody(props: AdvertisementPreviewBodyProps) {
  const formatDateTime = useDateTimeFormatter();
  const isBlockedByMandatoryFields = !props.inAdvertisement && props.isPublishBlocked;
  const [areExtraPlatformsExpanded, setAreExtraPlatformsExpanded] = useState(false);
  const data = props.preview.data;
  const platformTitles = props.preview.platformTitles;
  const platformLabel = (code: AdvertisingPlatformCodeEnum) => platformTitles.get(code) ?? code;
  const platformOptions = data.platforms.map((platform) => ({
    value: platform.platformCode,
    label: platformLabel(platform.platformCode),
  }));
  const hiddenPlatformCount = Math.max(0, platformOptions.length - VISIBLE_PLATFORM_COUNT);
  const selectedPlatformIndex = platformOptions.findIndex(
    (option) => option.value === props.selection.activePlatformCode
  );
  const hiddenSelectedPlatform = platformOptions[selectedPlatformIndex];
  const visiblePlatformOptions =
    !areExtraPlatformsExpanded &&
    selectedPlatformIndex >= VISIBLE_PLATFORM_COUNT &&
    isNotNil(hiddenSelectedPlatform)
      ? [...platformOptions.slice(0, VISIBLE_PLATFORM_COUNT - 1), hiddenSelectedPlatform]
      : platformOptions.slice(0, VISIBLE_PLATFORM_COUNT);
  const extraPlatformOptions = platformOptions.filter(
    (option) =>
      !visiblePlatformOptions.some((visibleOption) => visibleOption.value === option.value)
  );
  const handlePlatformChange = (selected: string[] | Nullish) => {
    const platform = data.platforms.find((item) => item.platformCode === selected?.[0]);
    if (isNotNil(platform)) {
      props.onPlatformChange(platform.platformCode);
    }
  };
  const selectedPlatform = data.platforms.find(
    (platform) => platform.platformCode === props.selection.activePlatformCode
  );
  const fuelLabel = props.preview.isVehicleCatalogueLoading
    ? undefined
    : props.preview.vehicleCatalogue.getFuelType(data.vehicle.fuelType);
  const transmissionLabel = props.preview.isVehicleCatalogueLoading
    ? undefined
    : props.preview.vehicleCatalogue.getTransmission(data.vehicle.transmission);
  const defaultLanguage = data.languages.find((language) => language.isDefault);
  const activeLanguage =
    props.selection.requestedLanguage ?? defaultLanguage?.language ?? data.language;
  const activeLanguageDetails = data.languages.find(
    (language) => language.language === activeLanguage
  ) ??
    defaultLanguage ??
    data.languages[0] ?? {
      language: data.language,
      isDefault: true,
      published: true,
    };
  const activePanelTestId = `${props['data-testid']}-${props.selection.activePlatformCode}`;
  const languageDisplayNames = new Intl.DisplayNames([i18n.language ?? 'en'], {type: 'language'});
  const languageLabel = (language: string) => {
    try {
      const label = languageDisplayNames.of(language) ?? language;
      return `${label.charAt(0).toLocaleUpperCase(i18n.language)}${label.slice(1)}`;
    } catch {
      return language;
    }
  };
  const languageOptions = data.languages.map((language) => {
    const localizedLanguageContent = props.selection.localizedContentByLanguage[language.language];
    const status = getLanguageContentStatus(localizedLanguageContent);

    return {
      value: language.language,
      label: (
        <LanguageOptionLabel>
          {languageLabel(language.language)}
          <VisuallyHiddenLabel>
            {i18n.t(
              match(status)
                .with('complete', () => 'page.advertisement.preview.languageDotsTooltipComplete')
                .with('partial', () => 'page.advertisement.preview.languageDotsTooltipPartial')
                .with('empty', () => 'page.advertisement.preview.languageDotsTooltipEmpty')
                .exhaustive()
            )}
          </VisuallyHiddenLabel>
          <LanguageStatusDot
            $status={status}
            data-status={status}
            data-testid={`${activePanelTestId}-language-status-${language.language}`}
          />
        </LanguageOptionLabel>
      ),
    };
  });
  const publishedDates =
    props.preview.salesPlatforms
      ?.filter(
        (platform) =>
          platform.status === AdvertisementStatusEnum.PUBLISHED &&
          isNotNil(platform.statusTriggeredAt)
      )
      .map((platform) => platform.statusTriggeredAt)
      .filter(isNotNil)
      .sort() ?? [];
  const lastPublishedAt = publishedDates.at(-1);

  return (
    <PreviewShell>
      <PlatformStrip>
        <PlatformTabsScroller data-testid={`${props['data-testid']}-platform-tabs-scroller`}>
          <VStack spacing={3}>
            <HStack align="center" spacing={2} wrap>
              <Chips
                size="default"
                options={visiblePlatformOptions}
                value={
                  isNotNil(props.selection.activePlatformCode)
                    ? [props.selection.activePlatformCode]
                    : []
                }
                onChange={handlePlatformChange}
                data-testid={`${props['data-testid']}-platforms`}
              />
              <Show when={hiddenPlatformCount > 0}>
                <MorePlatformsButton
                  type="button"
                  aria-expanded={areExtraPlatformsExpanded}
                  aria-controls={`${props['data-testid']}-extra-platforms`}
                  onClick={() => setAreExtraPlatformsExpanded(!areExtraPlatformsExpanded)}
                >
                  {i18n.t(
                    areExtraPlatformsExpanded
                      ? 'page.advertisement.preview.lessPlatforms'
                      : 'page.advertisement.preview.morePlatforms',
                    {count: hiddenPlatformCount}
                  )}
                  <Icon
                    value={
                      areExtraPlatformsExpanded
                        ? 'navigation/expand_less'
                        : 'navigation/expand_more'
                    }
                    size={4}
                  />
                </MorePlatformsButton>
              </Show>
            </HStack>
            <Show when={areExtraPlatformsExpanded}>
              <Box id={`${props['data-testid']}-extra-platforms`}>
                <Chips
                  size="default"
                  options={extraPlatformOptions}
                  value={
                    isNotNil(props.selection.activePlatformCode)
                      ? [props.selection.activePlatformCode]
                      : []
                  }
                  onChange={handlePlatformChange}
                  data-testid={`${props['data-testid']}-extra-platforms-chips`}
                />
              </Box>
            </Show>
          </VStack>
        </PlatformTabsScroller>
      </PlatformStrip>
      <PreviewCanvas>
        <Toolbar>
          <ToolbarLabel>
            <Text size="xSmall" color="secondary" noWrap>
              {i18n.t('page.advertisement.preview.previewFor')}{' '}
              <Text inline size="xSmall" color="primary" alternative>
                {isNotNil(props.selection.activePlatformCode)
                  ? platformLabel(props.selection.activePlatformCode)
                  : ''}
              </Text>
            </Text>
          </ToolbarLabel>
          <HStack align="center" spacing={3}>
            <Icon value="action/language" size={4} />
            <Text size="xSmall" color="secondary">
              {languageLabel(activeLanguage)}
            </Text>
          </HStack>
        </Toolbar>
        <AdvertisementPreviewContent
          key={props.selection.activePlatformCode}
          preview={{
            data,
            photosQuery: props.preview.photosQuery,
            pricingVehicle: props.preview.pricingVehicle,
            specificationsVehicle: props.preview.specificationsVehicle,
            fuelLabel,
            transmissionLabel,
            isVehicleCatalogueLoading: props.preview.isVehicleCatalogueLoading,
          }}
          selection={{
            selectedPlatform,
            isDefaultLanguage: activeLanguageDetails.isDefault,
            isLanguagePublished: activeLanguageDetails.published,
            isTextFetching: props.isLanguageSwitchFetch,
            languageOptions,
            activeLanguage,
            showLanguageTabs: props.hasLanguageTabs,
          }}
          onLanguageChange={(language) =>
            props.onLanguageChange(language === defaultLanguage?.language ? undefined : language)
          }
          onAddPhotos={props.onAddPhotos}
          vehicleId={props.vehicleId}
          data-testid={activePanelTestId}
        />
      </PreviewCanvas>
      <DialogFooter>
        <FooterLayout>
          <Text color="secondary" size="small">
            <Show when={isNotNilOrEmpty(lastPublishedAt) ? lastPublishedAt : null}>
              {(lastPublishedAt) => (
                <>
                  {i18n.t('page.advertisement.preview.lastPublished', {
                    date: formatDateTime('dateShort', parseDate(lastPublishedAt)),
                  })}
                  {' · '}
                </>
              )}
            </Show>
            {i18n.t('page.advertisement.preview.footerCounts', {
              platformCount: data.platforms.length,
              languageCount: data.languages.length,
            })}
          </Text>
          <HStack justify="flex-end" spacing={2}>
            <Button
              variant="secondary"
              title={i18n.t('general.actions.close')}
              onClick={props.onClose}
            />
            <Tooltip
              label={i18n.t('page.advertisement.preview.publishDisabled')}
              isDisabled={
                data.canPublish === true &&
                (props.inAdvertisement ? props.hasUpdatePermission : props.hasPublishPermission)
              }
            >
              <Button
                title={i18n.t(
                  props.inAdvertisement
                    ? 'page.advertisement.actions.updateAd'
                    : 'page.advertisement.actions.publish'
                )}
                variant="primary"
                onClick={props.onSubmit}
                isDisabled={
                  data.canPublish !== true ||
                  isBlockedByMandatoryFields ||
                  !(props.inAdvertisement ? props.hasUpdatePermission : props.hasPublishPermission)
                }
                isLoading={props.isSubmitting || isBlockedByMandatoryFields}
                data-testid={`${props['data-testid']}-submit`}
              />
            </Tooltip>
          </HStack>
        </FooterLayout>
      </DialogFooter>
    </PreviewShell>
  );
}

const PreviewShell = styled.div`
  container: preview-dialog / inline-size;
  min-width: 0;
`;
const PlatformStrip = styled.div`
  padding: ${({theme}) => theme.getSize(3)} ${({theme}) => theme.getSize(7)};
  background: ${({theme}) => theme.colors.palettes.white[10][100]};
  border-bottom: 1px solid ${({theme}) => theme.colors.general.separator};
  @container preview-dialog (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.dialogMedium
  )}) {
    padding-inline: ${({theme}) => theme.getSize(4)};
  }
  @container preview-dialog (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.dialogCompact
  )}) {
    padding: ${({theme}) => theme.getSize(3)};
  }
`;
const PreviewCanvas = styled.div`
  padding: 0 ${({theme}) => theme.getSize(7)} ${({theme}) => theme.getSize(7)};
  background: ${({theme}) => theme.colors.palettes.neutral[10][100]};
  @container preview-dialog (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.dialogMedium
  )}) {
    padding-inline: ${({theme}) => theme.getSize(4)};
  }
  @container preview-dialog (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.dialogCompact
  )}) {
    padding-inline: ${({theme}) => theme.getSize(3)};
  }
`;
const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: ${PREVIEW_STICKY_Z_INDEX.toolbar};
  height: ${({theme}) => theme.getSize(12)};
  gap: ${({theme}) => theme.getSize(3)};
  background: ${({theme}) => theme.colors.palettes.neutral[10][100]};
`;
const ToolbarLabel = styled.div`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const MorePlatformsButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({theme}) => theme.getSize(1)};
  padding: ${({theme}) => theme.getSize(1)} ${({theme}) => theme.getSize(2)};
  border: 1px solid ${({theme}) => theme.colors.general.separator};
  border-radius: ${({theme}) => theme.radii.large};
  background: ${({theme}) => theme.colors.palettes.white[10][100]};
  color: ${({theme}) => theme.colors.general.accent};
  cursor: pointer;
  user-select: none;
`;
const FooterLayout = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({theme}) => theme.getSize(4)};
  @container preview-dialog (max-width: ${getPreviewContainerBreakpoint(
    PREVIEW_CONTAINER_BREAKPOINTS.dialogCompact
  )}) {
    flex-wrap: wrap;
    justify-content: flex-end;
  }
`;
